// apps/dashboard/src/routes/targets/index.tsx
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Button, RouteShell } from "@/design-system";
import { listTargets } from "@/api/client";
import { PageChrome } from "@/components/chrome/PageChrome";
import { TargetsTable } from "@/components/targets/TargetsTable";
import { GettingStartedWidget } from "@/components/dashboard/GettingStartedWidget";
import { useScanKickoff } from "@/components/targets/scans/useScanKickoff";
import { inFlightInterval } from "@/hooks/useInFlightPolling";
import { hasInFlightScan } from "@/lib/scan-status";
import { useTargetDraft } from "@/lib/target-draft-store";
import { useOrganization } from "@clerk/react";

export const Route = createFileRoute("/targets/")({
  component: TargetsPage,
});

function TargetsPage() {
  const navigate = useNavigate();
  const clearDraft = useTargetDraft((s) => s.clearDraft);
  const organization = useOrganization();
  const { requestScan, dialog } = useScanKickoff();

  const { data, isPending, error } = useQuery({
    queryKey: ["targets"],
    queryFn: ({ signal }) => listTargets(signal),
    refetchInterval: (q) =>
      inFlightInterval(q.state.data, (d) => d.targets.some(hasInFlightScan)),
  });

  const startNewTarget = () => {
    clearDraft();
    void navigate({ to: "/targets/new" });
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <PageChrome
        crumbs={[
          { label: organization.organization?.name || "", to: "/dashboard" },
          { label: "Servers" },
        ]}
        title="Servers"
        helper="MCP endpoints your org can scan"
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={startNewTarget}
            className="shrink-0"
          >
            <Plus size={13} strokeWidth={2} />
            New server
          </Button>
        }
      />
      <div className="flex-1 overflow-y-auto flex flex-col gap-5 px-4 sm:px-7 py-6">
        <RouteShell isPending={isPending} error={error}>
          <GettingStartedWidget />
          {data && data.targets.length > 0 ? (
            <TargetsTable targets={data.targets} onScan={requestScan} />
          ) : null}
        </RouteShell>
      </div>
      {dialog}
    </div>
  );
}
